import useQueryParameters from 'hooks/useQueryParameters';
import useUAParser from 'hooks/useUAParser';
import log from 'loglevel';
import { useSelector } from 'react-redux';
import Coords from 'state/Coords';
import { selectTenant } from 'state/tenant';
import { useMemo } from 'react';

export enum GeolocationErrorType {
  Unsupported = 'unsupported',
  Denied = 'denied',
  Unavailable = 'unavailable',
  Timeout = 'timeout',
}

export class GeolocationError extends Error {
  public type: GeolocationErrorType;

  public constructor(type: GeolocationErrorType) {
    super(`Geolocation error: ${type}`);
    this.type = type;
  }

  public static fromPositionError(
    positionError: PositionError,
  ): GeolocationError {
    const typeMap: { [code: number]: GeolocationErrorType } = {
      1: GeolocationErrorType.Denied,
      2: GeolocationErrorType.Unavailable,
      3: GeolocationErrorType.Timeout,
    };
    return new this(typeMap[positionError.code]);
  }
}

const getCurrentPosition = (): Promise<Coords> => {
  if (!('geolocation' in navigator)) {
    return Promise.reject(
      new GeolocationError(GeolocationErrorType.Unsupported),
    );
  }

  const promise = new Promise<Coords>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      position => resolve(position.coords),
      error => reject(GeolocationError.fromPositionError(error)),
      { timeout: 5000 },
    );
  });

  return promise;
};

const safeGetCurrentPosition = (): Promise<Coords> | undefined => {
  try {
    return getCurrentPosition();
  } catch (error) {
    if (error instanceof GeolocationError) {
      log.error(error);
    } else {
      throw error;
    }
  }
};

type GeolocationHook = () => [boolean, typeof safeGetCurrentPosition];

const useGeolocation: GeolocationHook = () => {
  const {
    enabledFeatures: { storeAttribution },
    geolocationAttribution,
    overrideConfig: { inStore },
  } = useSelector(selectTenant);
  const { token } = useQueryParameters();

  const {
    device: { type },
  } = useUAParser();

  const shouldGeolocate = useMemo(() => {
    const attributionEnabled =
      storeAttribution || Boolean(geolocationAttribution);

    // This applied to all workflow that pass in a token
    const isMobileTokenLookupWorkflow = Boolean(token) && type === 'mobile';

    return attributionEnabled && (inStore || isMobileTokenLookupWorkflow);
  }, [geolocationAttribution, inStore, storeAttribution, token, type]);

  return [shouldGeolocate, safeGetCurrentPosition];
};

export default useGeolocation;
